import { useEffect, useState } from "react";
import { API_BASE } from "../lib/api";

type Status = "checking" | "ok" | "degraded" | "down";

export function SystemStatusBadge() {
  const [status, setStatus] = useState<Status>("checking");

  useEffect(() => {
    let alive = true;
    let timer: number | null = null;

    async function poll(): Promise<void> {
      try {
        const ctrl = new AbortController();
        const t = window.setTimeout(() => ctrl.abort(), 8_000);
        const res = await fetch(`${API_BASE}/api/health`, {
          signal: ctrl.signal,
        });
        window.clearTimeout(t);
        if (!alive) return;
        if (res.ok) {
          const data = (await res.json()) as { status?: Status };
          setStatus(data.status === "ok" ? "ok" : "degraded");
        } else if (res.status === 503) {
          const data = (await res.json().catch(() => null)) as
            | { status?: Status }
            | null;
          setStatus(data?.status === "down" ? "down" : "degraded");
        } else {
          setStatus("degraded");
        }
      } catch {
        if (alive) setStatus("down");
      }
    }

    void poll();
    timer = window.setInterval(() => void poll(), 90_000);
    return () => {
      alive = false;
      if (timer != null) window.clearInterval(timer);
    };
  }, []);

  const cfg = {
    checking: { dot: "bg-ink-300", label: "Checking…", color: "text-ink-500" },
    ok: { dot: "bg-emerald-500", label: "All systems normal", color: "text-emerald-700" },
    degraded: {
      dot: "bg-amber-500",
      label: "Some services degraded",
      color: "text-amber-700",
    },
    down: {
      dot: "bg-red-500 animate-pulse",
      label: "Service issue detected",
      color: "text-red-700",
    },
  }[status];

  return (
    <div className="mb-2 flex items-center gap-2 px-1 text-2xs">
      <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
      <span className={cfg.color}>{cfg.label}</span>
    </div>
  );
}
